import type { RefObject } from 'react';
import { PAPIERS_DE_FICHE, TYPES_DE_FICHE } from '@nextilt/commun';
import type { Bulle } from '../partage/api/types';
import { ChampsQuand } from './ChampsQuand';
import { PoserUneCondition, useConditionsDeLaBulle } from './Dependances';
import { EtapesDeLaFiche } from './EtapesDeLaFiche';
import { EtoilesDeDifficulte } from './EtoilesDeDifficulte';
import { piecesJointes } from './ficheEnMots';
import type { TypeEtPapierDeLaFiche } from './HautDeLaFiche';
import { PieceAPoser } from './PiecesDeLaFiche';
import { groupesDesReglages, type GroupeDeReglages, type Reglage, type SorteDeReglage } from './reglagesDuTelephone';
import { ChoixDeLaDuree, ChoixDeLImportance, Plus } from './ReglagesDeLaFiche';
import { Choix, Ligne } from './Saisies';
import {
  cransDeCanal,
  CREUSER,
  DEPLACER,
  ELLE_RETIENT,
  EN_COURS_DE_DEVELOPPEMENT,
  enleverLeReglage,
  FICHE_PAS_ECRITE,
  IDEE,
  IDEE_EXPLIQUEE,
  JE_PEUX_LA_FAIRE,
  JOINDRE,
  LA_TACHE,
  MOTS_DES_PAPIERS_DE_FICHE,
  MOTS_DES_TYPES_DE_FICHE,
  OUVRIR_LE_LIEN,
  PAPIER_DE_FICHE,
  PLANS_DE_VIE,
  QUAND_ET_CONDITIONS,
  SUPPRIMER,
  TACHE,
  TYPE_DE_FICHE,
} from '../partage/vocabulaire';
import './NatureDeLaBulle.css';
import './ReglagesAuPouce.css';

/** Le réglage ouvert sous son groupe, un seul à la fois dans le panneau. */
export type ReglageAuPouce = Exclude<SorteDeReglage, 'heures' | 'attente' | 'heritee'> | null;

const NOMS_DES_GROUPES: Record<GroupeDeReglages['nom'], string> = {
  tache: LA_TACHE,
  quand: QUAND_ET_CONDITIONS,
  joindre: JOINDRE,
};

/** Les repères du tuto et des scripts sur les pastilles en pointillés (les mêmes que « Ajouter »). */
const REPERES: Partial<Record<SorteDeReglage, string>> = {
  difficulte: 'fiche-difficulte',
  echeance: 'ajouter-echeance',
  rdv: 'ajouter-rendez-vous',
  delai: 'ajouter-delai',
  condition: 'ajouter-condition',
  lien: 'ajouter-lien',
  adresse: 'ajouter-adresse',
  contact: 'ajouter-contact',
};

/**
 * Le panneau Réglages de la fiche au téléphone (chantier `telephone-projets-fiche`, ticket 04,
 * `design/telephone.html`, « Le panneau Réglages, complet » ; revue, décision 15 ; cadrage,
 * décisions 12, 19 et 20). Il remplace, au téléphone seulement, le panneau de l'ordinateur posé
 * dans une feuille : `ReglagesDeLaFiche` et les blocs de la fiche de l'ordinateur ne changent pas.
 *
 * Trois groupes aux couleurs de la fiche (`reglagesDuTelephone.ts`) : « La tâche » (Tâche ou Idée en
 * tête), « Quand et conditions » à l'ambre, « Joindre » au bleu-vert. Ce qui est réglé est plein,
 * sa croix l'enlève ; ce qui ne l'est pas est en pointillés. Toucher une pastille ouvre son
 * réglage sous le groupe, avec les champs d'aujourd'hui (les crans, les étoiles, les dates, la
 * condition à chercher, la pièce à poser) ; « Aux heures d'ouverture » se pose d'un toucher.
 *
 * En tête, le type de la fiche et, pour une Classique, son papier (chantier telephone-fiche, 01,
 * maquette `reglages()`) : le choix s'écrit comme à l'ordinateur, et la fiche se redessine.
 *
 * Après les groupes, « Ses étapes » (la liste, le champ qui en ajoute une, « Creuser ») : ce que
 * le panneau d'avant permettait, en attendant le + du ticket 05. Tout en bas, à part,
 * « Déplacer › » et « Supprimer », en rouge : la seule exception à « pas de rouge » (décision 15).
 */
export function ReglagesAuPouce({
  bulle,
  fiche,
  ouvert,
  onOuvrir,
  onChanger,
  onEtape,
  onCreuser,
  onDeplacer,
  onSupprimer,
  boutonDeplacer,
  boutonSupprimer,
}: {
  bulle: Bulle;
  /** Le type et le papier de la fiche, et le geste qui les change ; sans lui, pas de ligne du type. */
  fiche?: TypeEtPapierDeLaFiche;
  ouvert: ReglageAuPouce;
  onOuvrir: (reglage: ReglageAuPouce) => void;
  onChanger: (champs: Partial<Bulle>) => void;
  onEtape?: (id: string) => void;
  onCreuser: () => void;
  onDeplacer: () => void;
  onSupprimer: () => void;
  boutonDeplacer: RefObject<HTMLButtonElement | null>;
  boutonSupprimer: RefObject<HTMLButtonElement | null>;
}) {
  const conditions = useConditionsDeLaBulle(bulle.id);
  const groupes = groupesDesReglages(
    bulle,
    conditions.attend.map((l) => ({ lienId: l.lienId, titre: l.titre }))
  );
  const etat = bulle.etat;
  const idee = etat === 'sans_objet';
  const fermer = () => onOuvrir(null);

  /* Toucher une pastille : son réglage s'ouvre, ou se replie s'il l'était. */
  function toucher(r: Reglage) {
    if (r.sorte === 'heures') {
      if (!r.regle) onChanger({ heuresOuvrables: true });
      return;
    }
    if (r.sorte === 'attente' || r.sorte === 'heritee') return;
    onOuvrir(ouvert === r.sorte ? null : r.sorte);
  }
  function enlever(r: Reglage) {
    if (r.lienId) conditions.delier(r.lienId);
    else if (r.enlever) onChanger(r.enlever);
    if (ouvert === r.sorte) fermer();
  }

  return (
    <div className="panneau-fiche">
      {fiche && <TypeEtPapier fiche={fiche} />}

      {groupes.map((g) => (
        <section key={g.nom} className="panneau-fiche-groupe" data-groupe={g.nom} aria-label={NOMS_DES_GROUPES[g.nom]}>
          <h3 className="panneau-fiche-nom">{NOMS_DES_GROUPES[g.nom]}</h3>

          {g.nom === 'tache' && (
            <div className="nature-bascule panneau-fiche-nature" role="group" aria-label="Tâche ou idée">
              <button type="button" aria-pressed={!idee} onClick={() => idee && onChanger({ etat: 'a_faire' })}>
                {TACHE}
              </button>
              <button
                type="button"
                data-tuto="fiche-idee"
                aria-pressed={idee}
                title={IDEE_EXPLIQUEE}
                onClick={() => !idee && onChanger({ etat: 'sans_objet' })}
              >
                {IDEE}
              </button>
            </div>
          )}

          <div className="panneau-fiche-pastilles" data-tuto={g.nom === 'tache' ? 'fiche-parametres' : undefined}>
            {g.reglages.map((r) => (
              <Pastille key={r.cle} reglage={r} ouverte={ouvert === r.sorte} onToucher={() => toucher(r)} onEnlever={() => enlever(r)} />
            ))}
            {g.nom === 'joindre' && (
              <span className="pastille-reglage pastille-reglage--vide pastille-reglage--plus-tard" data-sorte="plans">
                <span className="pastille-reglage-texte">
                  {PLANS_DE_VIE} <span className="pastille-reglage-dev">· {EN_COURS_DE_DEVELOPPEMENT}</span>
                </span>
              </span>
            )}
          </div>

          {g.nom === 'quand' && conditions.retient.length > 0 && (
            <p className="panneau-fiche-retient">
              {ELLE_RETIENT} : {conditions.retient.map((l) => l.titre).join(', ')}
            </p>
          )}

          {ouvert && ouvert !== 'condition' && groupeDe(ouvert) === g.nom && (
            <div className="panneau-fiche-reglage">
              <LeReglage bulle={bulle} ouvert={ouvert} onChanger={onChanger} onFini={fermer} />
            </div>
          )}

          {/* Toujours là : le refus d'une boucle s'y montre après la recherche refermée. */}
          {g.nom === 'quand' && (
            <PoserUneCondition
              bulleId={bulle.id}
              titre={bulle.titre}
              delaiJours={bulle.delaiJours}
              ouvert={ouvert === 'condition'}
              onFermer={fermer}
            />
          )}
        </section>
      ))}

      <div className="panneau-fiche-etapes">
        {onEtape ? (
          <EtapesDeLaFiche bulleId={bulle.id} onEtape={onEtape} onCreuser={onCreuser} />
        ) : (
          <button type="button" className="bouton bouton-discret etapes-creuser" onClick={onCreuser}>
            {CREUSER}
          </button>
        )}
      </div>

      <div className="panneau-fiche-ranger">
        <button ref={boutonDeplacer} type="button" className="panneau-fiche-geste" data-tuto="fiche-deplacer" onClick={onDeplacer}>
          {DEPLACER}
          <span aria-hidden="true"> ›</span>
        </button>
        <button
          ref={boutonSupprimer}
          type="button"
          className="panneau-fiche-geste panneau-fiche-supprimer"
          onClick={onSupprimer}
        >
          {SUPPRIMER}
        </button>
      </div>
    </div>
  );
}

/**
 * Le type de la fiche, en crans (Classique, Note, Contact, Plan), et pour une Classique son papier.
 * Toucher le cran déjà choisi ne change rien : il n'y a pas de « rien » à choisir.
 */
function TypeEtPapier({ fiche }: { fiche: TypeEtPapierDeLaFiche }) {
  const { montree, pasEcrit, onChoisir } = fiche;
  return (
    <section className="panneau-fiche-groupe" data-groupe="fiche" aria-label={TYPE_DE_FICHE}>
      <Ligne nom={TYPE_DE_FICHE}>
        <Choix
          valeur={montree.type}
          options={TYPES_DE_FICHE.map((t) => [t, MOTS_DES_TYPES_DE_FICHE[t]] as const)}
          onChoisir={(t) => t && t !== montree.type && onChoisir({ typeDeFiche: t })}
        />
      </Ligne>
      {montree.type === 'classique' && (
        <Ligne nom={PAPIER_DE_FICHE}>
          <Choix
            valeur={montree.papier}
            options={PAPIERS_DE_FICHE.map((p) => [p, MOTS_DES_PAPIERS_DE_FICHE[p]] as const)}
            onChoisir={(p) => p && p !== montree.papier && onChoisir({ papierDeFiche: p })}
          />
        </Ligne>
      )}
      {pasEcrit && (
        <p className="panneau-fiche-retient" role="alert">
          {FICHE_PAS_ECRITE}
        </p>
      )}
    </section>
  );
}

/** Le groupe sous lequel un réglage s'ouvre. */
function groupeDe(sorte: Exclude<ReglageAuPouce, null>): GroupeDeReglages['nom'] {
  if (sorte === 'duree' || sorte === 'ou' || sorte === 'importance' || sorte === 'difficulte') return 'tache';
  if (sorte === 'adresse' || sorte === 'contact' || sorte === 'lien') return 'joindre';
  return 'quand';
}

/**
 * Une pastille : pleine avec sa valeur et sa croix, ou en pointillés avec « + ». La valeur se
 * touche (son réglage s'ouvre), la croix aussi (il s'enlève) ; une attente héritée se lit
 * seulement : elle se retire depuis la bulle qui la porte.
 */
function Pastille({
  reglage: r,
  ouverte,
  onToucher,
  onEnlever,
}: {
  reglage: Reglage;
  ouverte: boolean;
  onToucher: () => void;
  onEnlever: () => void;
}) {
  const seLit = r.sorte === 'heritee' || (r.regle && (r.sorte === 'heures' || r.sorte === 'attente'));
  /* Le signe, la valeur et d'où elle vient se lisent d'un tenant, et passent à la ligne ensemble. */
  const contenu = (
    <>
      {!r.regle && <Plus />}
      <span>
        {r.regle && r.signe && <span aria-hidden="true">{r.signe} </span>}
        {r.texte}
        {r.mention && <span className="pastille-reglage-mention"> {r.mention}</span>}
      </span>
    </>
  );
  const aCroix = r.regle && (r.enlever !== null || !!r.lienId);
  return (
    <span
      className={`pastille-reglage${r.regle ? ' pastille-reglage--regle' : ' pastille-reglage--vide'}`}
      data-sorte={r.sorte}
    >
      {seLit ? (
        <span className="pastille-reglage-texte">{contenu}</span>
      ) : (
        <button
          type="button"
          className="pastille-reglage-valeur"
          data-tuto={r.regle ? undefined : REPERES[r.sorte]}
          aria-expanded={r.sorte === 'heures' ? undefined : ouverte}
          aria-pressed={r.sorte === 'heures' ? false : undefined}
          onClick={onToucher}
        >
          {contenu}
        </button>
      )}
      {aCroix && (
        <button type="button" className="pastille-reglage-croix" aria-label={enleverLeReglage(r.sorte === 'attente' ? r.texte : r.nom)} onClick={onEnlever}>
          <span aria-hidden="true">×</span>
        </button>
      )}
    </span>
  );
}

/** Le réglage ouvert : les champs d'aujourd'hui, un à la fois. */
function LeReglage({
  bulle,
  ouvert,
  onChanger,
  onFini,
}: {
  bulle: Bulle;
  ouvert: Exclude<ReglageAuPouce, null | 'condition'>;
  onChanger: (champs: Partial<Bulle>) => void;
  onFini: () => void;
}) {
  switch (ouvert) {
    case 'duree':
      return (
        <div className="champs parametres" role="group" aria-label="réglage de la bulle" data-tuto="parametres">
          <ChoixDeLaDuree bulle={bulle} onChanger={onChanger} onFini={onFini} />
        </div>
      );
    case 'ou':
      return (
        <div className="champs parametres" role="group" aria-label="réglage de la bulle" data-tuto="parametres">
          <Ligne nom={JE_PEUX_LA_FAIRE} aide="D’où tu peux la faire. Les heures d’ouverture se posent à part, juste au-dessus.">
            <Choix
              valeur={bulle.canal}
              options={cransDeCanal()}
              onChoisir={(canal) => onChanger({ canal })}
            />
          </Ligne>
        </div>
      );
    case 'importance':
      return (
        <div className="champs parametres" role="group" aria-label="réglage de la bulle" data-tuto="parametres">
          <ChoixDeLImportance bulle={bulle} onChanger={onChanger} />
        </div>
      );
    case 'difficulte':
      return (
        <div className="champs parametres" role="group" aria-label="réglage de la bulle">
          <Ligne nom="Difficulté pour toi" aide="1 étoile = facile, 5 étoiles = difficile. Re-touche la même étoile pour la retirer.">
            <EtoilesDeDifficulte valeur={bulle.charge} onChoisir={(charge) => onChanger({ charge })} />
          </Ligne>
        </div>
      );
    case 'echeance':
    case 'rdv':
    case 'delai':
      return <ChampsQuand key={ouvert} bulle={bulle} onChanger={onChanger} seulement={ouvert} onFini={onFini} />;
    case 'adresse':
    case 'contact':
    case 'lien': {
      const lien = ouvert === 'lien' ? piecesJointes(bulle).find((p) => p.sorte === 'lien') : undefined;
      return (
        <PieceAPoser key={ouvert} sorte={ouvert} bulle={bulle} onChanger={onChanger} onFini={onFini}>
          {lien && bulle.lien && (
            <a className="panneau-fiche-ouvrir" href={bulle.lien} target="_blank" rel="noopener noreferrer">
              {OUVRIR_LE_LIEN}
              <span aria-hidden="true"> ↗</span>
            </a>
          )}
        </PieceAPoser>
      );
    }
  }
}
